/**
 * useStep3DamageScenarios — Step 3 lifecycle.
 *
 * Composes the shared sub-flow hooks into Step 3's body-props contract:
 *   - `useStructuredFetch` × 2 — damage-scenario list + asset list
 *     (modal's `asset_ids` multi-select)
 *   - `useRecordCRUD`           — add / update / delete via the workflow
 *     record endpoints
 *   - `useHierarchicalApprove`  — child-scenario relevance + the approve
 *     closure that routes the global Approve through
 *     `approveHierarchicalData(... primaryKey='damage_id')`
 *
 * Wire shape — note the difference from Step 2: damage scenarios live
 * at `raw.result.damage_scenarios` (outer wrapper is `{ timestamp,
 * agent_name, extraction_method, damage_scenarios }`).
 *
 * Hierarchical classification:
 *   The backend tags inherited rows with `source: 'child_tara'` +
 *   `source_session_id` + (often) pre-set `is_relevant` /
 *   `relevance_justification`. Matches the legacy `HierarchicalStepReview`
 *   filter: child = `source === 'child_tara'`, current = everything
 *   else (incl. missing source). Sessions on older agent versions
 *   that don't emit `source` simply show all rows in the Current tab,
 *   which is the same behavior as legacy.
 */
import { useCallback } from 'react';
import { getAgentResult } from '../../../api/workflow';
import { useStructuredFetch } from '../hooks/useStructuredFetch';
import { useRefetchOnReviseComplete } from '../hooks/useRefetchOnReviseComplete';
import { useRecordCRUD } from '../hooks/useRecordCRUDv2';
import { useHierarchicalApprove } from '../hooks/useHierarchicalApprove';
import type { Step3BodyProps } from '../step-components/stepBodyProps';
import type {
  PerStepState,
  WorkflowActions,
  WorkflowV2State,
} from '../state/workflowV2Types';
import type { AssetOption } from '../step-components/step3-scenarios/ScenarioFormModal';

const HAS_OUTPUT = new Set(['draft', 'revising', 'approved']);
const AGENT_NAME = 'damage_scenario_agent';
const ASSET_AGENT_NAME = 'asset_identification_agent';

interface AgentResultResponse {
  result?: Record<string, unknown>;
  [key: string]: unknown;
}

interface AssetLite {
  asset_id?: string;
  asset_name?: string;
  [key: string]: unknown;
}

export interface UseStep3Input {
  threadId: string | null | undefined;
  stepState: PerStepState | undefined;
  workflowCompleted: boolean;
  /** Session-level `analysis_mode`. `'hierarchical'` flips Step 3 to
   *  the Current TARA / Child TARAs two-tab review and surfaces the
   *  approve-override that routes the global Approve through
   *  `approveHierarchicalData` rather than the default `resumeWorkflow`. */
  analysisMode?: string | null;
  /** Reducer state — needed so `useHierarchicalApprove` can read
   *  `state.steps[3].hierarchicalModifications` (Story H2). */
  state: WorkflowV2State;
  /** Action creators bound to the reducer's dispatch. */
  actions: WorkflowActions;
}

export function useStep3DamageScenarios({
  threadId,
  stepState,
  workflowCompleted,
  analysisMode,
  state,
  actions,
}: UseStep3Input): Step3BodyProps {
  const status = stepState?.status ?? 'empty';
  const hasOutput = HAS_OUTPUT.has(status);

  /* ----- Damage scenarios fetch ----- */

  const fetchScenarios = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractScenarios = useCallback((raw: AgentResultResponse | null): unknown[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.damage_scenarios ?? r?.scenarios) as unknown[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const structured = useStructuredFetch<AgentResultResponse | null, unknown[]>({
    fetcher: fetchScenarios,
    extract: extractScenarios,
    enabled: hasOutput && !!threadId,
  });
  useRefetchOnReviseComplete(status, structured.refetch);

  /* ----- Asset list fetch (modal options) ----- */

  const editable = workflowCompleted || status === 'draft';

  const fetchAssets = useCallback(
    (_signal: AbortSignal): Promise<AgentResultResponse | null> => {
      if (!threadId) return Promise.resolve(null);
      return getAgentResult(threadId, ASSET_AGENT_NAME) as unknown as Promise<AgentResultResponse>;
    },
    [threadId],
  );

  const extractAssets = useCallback((raw: AgentResultResponse | null): AssetLite[] => {
    const r = raw?.result as Record<string, unknown> | undefined;
    const list = (r?.assets ?? r?.identified_assets) as AssetLite[] | undefined;
    return Array.isArray(list) ? list : [];
  }, []);

  const assetsFetch = useStructuredFetch<AgentResultResponse | null, AssetLite[]>({
    fetcher: fetchAssets,
    extract: extractAssets,
    enabled: editable && !!threadId,
  });

  const assetOptions: AssetOption[] = (assetsFetch.data ?? []).map((a) => ({
    value: String(a.asset_id ?? ''),
    label: a.asset_name ? `${a.asset_id} — ${a.asset_name}` : String(a.asset_id ?? ''),
  }));

  /* ----- CRUD ----- */

  const crud = useRecordCRUD({
    threadId,
    agentName: AGENT_NAME,
    enabled: editable,
  });

  /* ----- Hierarchical approve -----
   * Default `isChild` predicate (`r.source === 'child_tara'`) is the
   * legacy-compatible split. No custom predicate — keeps Steps 3-8
   * uniform. */

  const hierarchicalEnabled = analysisMode === 'hierarchical';
  const records = (structured.data ?? []) as Array<Record<string, unknown>>;
  const hierApprove = useHierarchicalApprove({
    stepId: 3,
    threadId,
    agentName: AGENT_NAME,
    enabled: hierarchicalEnabled,
    records,
    primaryKey: 'damage_id',
    state,
    actions,
    onApproved: structured.refetch,
  });

  return {
    status,
    errorMessage: stepState?.errorMessage ?? null,
    editable,
    structured,
    crud,
    assetOptions,
    hierarchical: {
      enabled: hierarchicalEnabled,
      modifications: hierApprove.modifications,
      setRelevance: hierApprove.setRelevance,
      setJustification: hierApprove.setJustification,
      onApprove: hierApprove.onApprove,
      approving: hierApprove.approving,
      approveError: hierApprove.approveError,
    },
    approveOverride: hierarchicalEnabled
      ? { onApprove: hierApprove.onApprove, approving: hierApprove.approving }
      : undefined,
  };
}
